import { Button, FormControl, Icon, IconButton, InputLabel, makeStyles, MenuItem, Select, TextField, Theme } from '@material-ui/core';
import { SelectInputOption } from '@moderepo/mode-app-components';
import { AttributeValueCompareOperator, EntityAttributeParam, EntityAttributeType, EntityAttributeValueType } from '@moderepo/mode-internal-apis';
import clsx from 'clsx';
import React, { CSSProperties, useCallback, useMemo } from 'react';
import { ARRAY_SUPPORTED_OPERATORS, AttributesQueryCondition } from '../../containers';
import { NonEmptyArray } from '../../utils/utils';


const useStyle = makeStyles((theme: Theme) => {
    return {
        root: {
            display      : 'flex',
            flexDirection: 'row',
            alignItems   : 'center',
            flexWrap     : 'wrap',
        },

        input: {
            marginRight: theme.spacing(1),
        },

        attributeInput: {
            minWidth: 200,
        },

        attributeMenuItem: {
            display       : 'flex',
            flexDirection : 'row',
            justifyContent: 'flex-start',
            alignContent  : 'center',
        },

        attributeMenuItemAttributeName: {
            color: theme.palette.text.primary,
        },

        attributeMenuItemAttributeType: {
            color        : theme.palette.text.secondary,
            fontSize     : 'small',
            paddingLeft  : theme.spacing(0.5),
            textTransform: 'capitalize',
        },

        operatorInput: {
            minWidth: 80,
        },

        valueInputWrapper: {
            position     : 'relative',
            display      : 'flex',
            flexDirection: 'row',
            alignItems   : 'center',
        },

        valueInput: {
            zIndex     : 0,
            width      : '100%',
            minWidth   : 100,
            '& .number': {
                width: 100,
            },
            '& .string': {
                width: 150,
            },
        },

        removeValueInputButton: {
            position : 'absolute',
            zIndex   : 1,
            transform: 'translateY(-50%)',
            right    : 0,
            top      : 0,
        },

        orLabel: {
            marginRight  : theme.spacing(1),
            textTransform: 'uppercase',
            whiteSpace   : 'nowrap',
        },
    };
});



// The maximum number of values the user can add to the array.
const MAX_VALUES_ARRAY = 5;



/**
 * A small reusable component for the remove value button
 */
const RemoveValueButton = (props: {
    className?: string | undefined;
    onClick: ()=> void;
}) => {
    return (
        <IconButton
            className={props.className}
            color="secondary"
            size="small"
            onClick={props.onClick}
        >
            <Icon fontSize="small">cancel</Icon>
        </IconButton>
    );
};



export interface AttributeConditionInputProps {
    readonly className?: string | undefined;
    readonly style?: CSSProperties | undefined;

    // A list of Attribute Options for the user to select an attribute
    readonly attributeOptions: readonly SelectInputOption<EntityAttributeParam<EntityAttributeValueType>>[];

    // Lists of operator options for the user to select an operator. These operators are grouped by attribute value type
    readonly operatorOptions: {
        readonly [EntityAttributeType.NUMBER]: readonly SelectInputOption<AttributeValueCompareOperator>[],
        readonly [EntityAttributeType.BOOLEAN]: readonly SelectInputOption<AttributeValueCompareOperator>[]
        readonly [EntityAttributeType.STRING]: readonly SelectInputOption<AttributeValueCompareOperator>[]
    };

    readonly texts: {
        readonly attributeNameLabel: string;
        readonly attributeValueLabel: string;
        readonly orButtonLabel: string;
        readonly booleanTrueValue: string;
        readonly booleanFalseValue: string;
        readonly attributeTypeLabel: {
            readonly [EntityAttributeType.NUMBER]: string;
            readonly [EntityAttributeType.STRING]: string;
            readonly [EntityAttributeType.BOOLEAN]: string;
        }
    };

    // The attribute condition value to initialize the inputs with
    readonly attributeQueryCondition: AttributesQueryCondition;

    readonly onAttributeChanged: (
        attribute: EntityAttributeParam<EntityAttributeValueType>
    )=> void;

    readonly onOperatorChanged: (
        inputs: AttributeValueCompareOperator
    )=> void;

    readonly onValuesChanged: (
        inputs: NonEmptyArray<EntityAttributeValueType | undefined>
    )=> void;
}



/**
 * This is a component to get the user inputs for ONE attribute condition. Each condition is consists of the attribute, the operator, and 1 or more
 * values to match the attribute values. Here are some Condition examples
 *
 *      type === ['car', 'truck']       // Filter entities which has attribute 'type' exactly equal 'car' or 'truck'
 *      make === ['toyota', 'honda']    // Filter entities which has attribute 'make' exactly equal 'toyota' or 'honda'
 *      year === 2020                   // Filter entities which has attribute 'year' equal 2020
 */
export const AttributeConditionInput: React.FC<AttributeConditionInputProps> = (props: AttributeConditionInputProps) => {

    const { attribute, operator, values } = props.attributeQueryCondition;
    const classes = useStyle();


    /**
     * Build a map of the attribute's keys and the number of time the keys exists in the attributeOptions. We will use this map to
     * determine if we need to show the attribute type next to the attribute label. If an attribute key exist more than 1 time then
     * we know there are multiple attributes with the same key therefore, we need to display attribute's type next to the label.
     */
    const attributeKeysDuplicateCountMap = useMemo(() => {
        const result: {
            [key: string]: number | undefined;
        } = {};
        props.attributeOptions.forEach((option) => {
            result[option.value.key] = (result[option.value.key] ?? 0) + 1;
        });
        return result;
    }, [props.attributeOptions]);


    /**
     * On attribute changed, notify the handler
     */
    const onAttributeChange = useCallback((event: React.ChangeEvent<{ value: unknown }>) => {
        // The Attribute Select control uses attribute option index as value so we need to get the Attribute config object from the index
        const newAttribute = event.target.value as EntityAttributeParam<EntityAttributeValueType>;
        props.onAttributeChanged(newAttribute);
    }, [props]);


    /**
     * On operator changed, notify the handler
     */
    const onOperatorChange = useCallback((event: React.ChangeEvent<{ value: unknown }>) => {
        const newOperator = event.target.value as AttributeValueCompareOperator;
        props.onOperatorChanged(newOperator);
    }, [props]);


    /**
     * On value changed, replace the value at index and notify the handler
     */
    const onValueChange = useCallback((value: EntityAttributeValueType, index: number) => {
        const newValuesArray: NonEmptyArray<EntityAttributeValueType | undefined> = [...values];
        newValuesArray[index] = value;
        props.onValuesChanged(newValuesArray);
    }, [props, values]);


    /**
     * On Add value button click, we will add a new element with value 'undefined' to the values array
     */
    const onAddValueClick = useCallback(() => {
        const newValuesArray: NonEmptyArray<EntityAttributeValueType | undefined> = [...values, undefined];
        props.onValuesChanged(newValuesArray);
    }, [props, values]);


    /**
     * On remove value button click, we will remove the element at the specified index
     */
    const onRemoveValueClick = useCallback((indexToRemove: number) => {
        const newValuesArray: NonEmptyArray<EntityAttributeValueType | undefined> = [...values];
        newValuesArray.splice(indexToRemove, 1);
        props.onValuesChanged(newValuesArray);
    }, [props, values]);


    /**
     * Compile a list of operators that can be used based on the attribute type. Some operator can't be used for some attribute type e.g.
     * operators <, <=, >, >= does not work with attribute of type STRING. So when ever a different attribute is selected, we need to
     * recompile a list of useable operators.
     */
    const useableOperators = useMemo(() => {
        if (attribute) {
            return props.operatorOptions[attribute.type];
        }
        return [];
    }, [attribute, props.operatorOptions]);


    return (
        <div className={clsx(props.className, classes.root)} style={props.style}>

            {/**
             * Add Attribute SELECT input field
             */}
            <FormControl variant="outlined" className={clsx(classes.input, classes.attributeInput)}>
                <InputLabel>{props.texts.attributeNameLabel}</InputLabel>
                <Select
                    label={props.texts.attributeNameLabel}
                    value={attribute ?? ''}
                    displayEmpty
                    onChange={onAttributeChange}
                    renderValue={(value) => {
                        return (value as EntityAttributeParam<EntityAttributeValueType>).label;
                    }}
                >
                    {props.attributeOptions.map((option) => {
                        const keyDuplicateCount = attributeKeysDuplicateCountMap[option.value.key];
                        return (
                            <MenuItem
                                key={`${option.value.key}-${option.value.label}-${option.value.type}`}
                                className={classes.attributeMenuItem}
                                value={option.value as any}
                                disabled={option.disabled}
                            >
                                <div className={classes.attributeMenuItemAttributeName}>{option.label}</div>
                                {keyDuplicateCount !== undefined && keyDuplicateCount > 1 && (
                                    <div className={classes.attributeMenuItemAttributeType}>
                                        ({props.texts.attributeTypeLabel[option.value.type]})
                                    </div>
                                )}
                            </MenuItem>
                        );
                    })}
                </Select>
            </FormControl>

            {/*
                Add Operator SELECT input but only if there are more than 1 useableOperators. If there is only 1 option, that option will
                automatically be selected and the user doesn't need to choose one.
             */}
            {attribute && useableOperators.length > 1 && (
                <FormControl
                    variant="outlined"
                    className={clsx(classes.input, classes.operatorInput)}
                    disabled={attribute === undefined}
                >
                    <InputLabel />
                    <Select
                        value={operator ?? ''}
                        displayEmpty
                        onChange={onOperatorChange}
                    >
                        {useableOperators.map((option: SelectInputOption<any>) => {
                            return (
                                <MenuItem key={`${option.label}-${option.value}`} value={option.value} disabled={option.disabled}>
                                    {option.label}
                                </MenuItem>
                            );
                        })}
                    </Select>
                </FormControl>
            )}

            {operator && attribute?.type === EntityAttributeType.BOOLEAN && (
                <div className={classes.valueInputWrapper}>
                    <FormControl
                        variant="outlined"
                        className={clsx(classes.input, classes.valueInput)}
                        disabled={attribute === undefined}
                    >
                        <InputLabel>{props.texts.attributeValueLabel}</InputLabel>
                        <Select
                            label={props.texts.attributeValueLabel}
                            // Select control doesn't support boolean so we will use 0/1 for false/true
                            value={values[0] !== undefined ? Number(values[0]) : ''}
                            displayEmpty
                            onChange={(event: React.ChangeEvent<{ value: unknown }>) => {
                                onValueChange(Boolean(event.target.value), 0);
                            }}
                        >
                            <MenuItem value={1}>
                                {props.texts.booleanTrueValue}
                            </MenuItem>
                            <MenuItem value={0}>
                                {props.texts.booleanFalseValue}
                            </MenuItem>
                        </Select>
                    </FormControl>
                </div>
            )}

            {/*
                If attribute type is NUMBER or STRING and has OPTIONS then we will use SELECT control so that the user can
                 just choose a value from a list
            */}
            {operator && (attribute?.type === EntityAttributeType.NUMBER || attribute?.type === EntityAttributeType.STRING)
                && attribute.options && attribute.options.length > 0 && (
                <>
                    {values?.map((value, index) => {
                        return (
                            <div
                                className={classes.valueInputWrapper}
                                // eslint-disable-next-line react/no-array-index-key
                                key={index}
                            >
                                {index > 0 && (
                                    <div className={classes.orLabel}>{props.texts.orButtonLabel}</div>
                                )}
                                <FormControl
                                    variant="outlined"
                                    className={clsx(classes.input, classes.valueInput)}
                                    disabled={attribute === undefined}
                                >
                                    <InputLabel>{props.texts.attributeValueLabel}</InputLabel>
                                    <Select
                                        label={props.texts.attributeValueLabel}
                                        value={value ?? ''}
                                        displayEmpty
                                        defaultValue=""
                                        onChange={(event: React.ChangeEvent<{ value: unknown }>) => {
                                            if (attribute.type === EntityAttributeType.NUMBER) {
                                                onValueChange(Number(event.target.value), index);
                                            } else if (attribute.type === EntityAttributeType.STRING) {
                                                onValueChange(event.target.value as string, index);
                                            }
                                        }}
                                    >
                                        {attribute?.options?.map((option) => {
                                            return (
                                                <MenuItem
                                                    key={option.label}
                                                    value={attribute.type === EntityAttributeType.NUMBER
                                                        ? Number(option.value) : option.value.toString()}
                                                >
                                                    {option.label}
                                                </MenuItem>
                                            );
                                        })}
                                    </Select>
                                </FormControl>

                                {/* Add Remove value button for values at index 1 or higher */}
                                {index > 0 && (
                                    <RemoveValueButton
                                        className={classes.removeValueInputButton}
                                        onClick={() => {
                                            onRemoveValueClick(index);
                                        }}
                                    />
                                )}
                            </div>
                        );
                    })}
                </>
            )}


            {/*
                If attribute type is NUMBER or STRING but DOES NOT have OPTIONS then we will use TextFields control
            */}
            {operator && (attribute?.type === EntityAttributeType.NUMBER || attribute?.type === EntityAttributeType.STRING)
                && (!attribute.options || attribute.options.length === 0) && (
                <>
                    {values?.map((value, index) => {
                        return (
                            <div
                                className={classes.valueInputWrapper}
                                // eslint-disable-next-line react/no-array-index-key
                                key={index}
                            >
                                {index > 0 && (
                                    <div className={classes.orLabel}>{props.texts.orButtonLabel}</div>
                                )}
                                <TextField
                                    disabled={attribute === undefined}
                                    className={clsx(classes.input, classes.valueInput)}
                                    variant="outlined"
                                    type={attribute && attribute.type === EntityAttributeType.NUMBER ? 'number' : 'text'}
                                    InputProps={{
                                        className: attribute.type,
                                    }}
                                    label={props.texts.attributeValueLabel}
                                    value={value ?? ''}
                                    onChange={(event: React.FormEvent) => {
                                        onValueChange((event.target as HTMLInputElement).value, index);
                                    }}
                                />

                                {/* Add Remove value button for values at index 1 or higher */}
                                {index > 0 && (
                                    <RemoveValueButton
                                        className={classes.removeValueInputButton}
                                        onClick={() => {
                                            onRemoveValueClick(index);
                                        }}
                                    />
                                )}
                            </div>
                        );
                    })}
                </>
            )}

            {/*
                If the selected operator is one of the operators that support ARRAY then show 'Add' button for the user to add more value
                to the array
             */}
            { operator && ARRAY_SUPPORTED_OPERATORS.includes(operator) && values.length < MAX_VALUES_ARRAY && (
                <Button
                    color="default"
                    size="small"
                    variant="contained"
                    onClick={onAddValueClick}
                >
                    {props.texts.orButtonLabel}
                </Button>
            )}
        </div>
    );
};
